/**
* API Fetch Controller
*/
import { defu } from 'defu'

export function useCustomFetch(url, options = {}, customOptions = {}) {
  const { $member, payload: payloadData, static: staticData } = useNuxtApp()
  
  const { confGlobal } = useConfig()
  const { requestHeaders } = useApi()
  const { useCustomCookie } = useCustomTools()

  const method = options.method?.toUpperCase() || 'GET'
  const isGet = method === 'GET'
  const headers = options.headers ? requestHeaders(options.headers) : requestHeaders()
  const key = options.key || `fetch-${method}-${url}`

  const defaultOptions = {
    method,
    headers,
    key: key,
    cache: isGet && customOptions?.maxAge ? 'default' : 'no-cache',
    server: $member?.user?.value?.email?.includes('@ipmedia.net') && useCustomCookie('disableCache').value ? false : true,
    watch: false,
    default: () => [],
    transform(input) {
      const userTransform = typeof options.transform === 'function' ? options.transform : (x) => x
      const transformed = userTransform(input)

      if (import.meta.client) {
        return {
          ...transformed,
          ...{ __ssr: false },
          fetchedAt: new Date()
        }
      }

      return {
        ...transformed,
        ...{ __ssr: true }
      }
    },
    getCachedData(key) {
      const data = payloadData.data[key] || staticData.data[key]
      const params = parseFlatParams(data?._meta?.links?.current)
      
      let stopCaching = false
      if (options?.query) {
        const filteredQuery = filterNonEmptyParams(options?.query)

        for (const key in params) {
          const a = filteredQuery[key]
          const b = params[key]

          const normA = a != null ? String(a) : null
          const normB = b != null ? String(b) : null

          if (normA !== normB) {
            stopCaching = true
          }
        }
      }

      if (!data || !customOptions?.maxAge || stopCaching) {
        return
      }

      const expirationDate = new Date(data.fetchedAt)
  
      expirationDate.setTime(expirationDate.getTime() + (customOptions?.maxAge || 10) * 1000)

      const isExpired = expirationDate.getTime() < Date.now()

      if (isExpired) {
        return
      }

      return data
    },
    onResponseError({ response }) {
      if ((response?._data?.data || response?._data?.message) && customOptions.errorToast !== false) {
        useEventQueue().add('toast', { severity: 'error', detail: response?._data?.data.toString() ? response?._data?.data.toString() : response?._data?.message, life: 5000 })
      }

      return response
    }
  }

  // Merge Objects
  const mergedOptions = defu(options, defaultOptions)

  // Params for useAsyncData
  const asyncDataOptions = {
    transform: mergedOptions.transform,
    key: mergedOptions.key,
    onResponseError: mergedOptions.onResponseError
  }

  // 🧠 Critical: SSR + GET → use useAsyncData (automatic payload)
  if (import.meta.server && isGet) {
    return useAsyncData(key, () =>
      $fetch(`${confGlobal.apiProxyUrl}${url}`, mergedOptions),
    asyncDataOptions
    )
  }

  // 🧠 On the client or for POST — standard useFetch
  return useFetch(`${confGlobal.apiProxyUrl}${url}`, mergedOptions)
}

/**
* Invalidate Cache
* Example:
* useInvalidateCache('customers/orders')
*/
export function useInvalidateCache(url) {
  const { requestHeaders } = useApi()

  return useFetch(`/api/invalidate/${url}`, {
    method: 'delete',
    headers: requestHeaders()
  })
}